import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '../../core/services/logger.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { navigateTo } from '../../core/utils/reload';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

interface BrowserErrorData {
  errorType: 'wasm-oom' | 'browser-unsupported' | 'unknown';
  browserName: string;
  availableMemory?: number;
  requiredMemory?: number;
  technicalDetails?: string;
}

@Component({
  selector: 'app-browser-error',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './browser-error.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './browser-error.component.css',
})
export class BrowserErrorComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private loggerService = inject(LoggerService);
  private logger = this.loggerService.getLogger('browser-error');
  private toast = inject(ToastService);
  private translate = inject(TranslationService);

  errorData: BrowserErrorData = {
    errorType: 'unknown',
    browserName: 'Unknown',
  };

  showTechnicalDetails = false;
  isUploadingLogs = signal(false);

  ngOnInit() {
    // Get error details from query params
    this.route.queryParams.subscribe((params) => {
      this.errorData = {
        errorType: params['type'] || 'unknown',
        browserName: params['browser'] || this.detectBrowser(),
        availableMemory: params['available'] ? parseFloat(params['available']) : undefined,
        requiredMemory: params['required'] ? parseFloat(params['required']) : undefined,
        technicalDetails: params['details'] || undefined,
      };
    });
  }

  private detectBrowser(): string {
    const ua = window.navigator.userAgent;
    if (ua.includes('Brave')) return 'Brave';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    return 'Unknown';
  }

  get isBrave(): boolean {
    return this.errorData.browserName === 'Brave';
  }

  get isWasmOom(): boolean {
    return this.errorData.errorType === 'wasm-oom';
  }

  reloadPage() {
    navigateTo('/', 'browser-error page reload');
  }

  copyTechnicalDetails() {
    if (this.errorData.technicalDetails) {
      window.navigator.clipboard.writeText(this.errorData.technicalDetails);
    }
  }

  get memoryShortfall(): number | undefined {
    if (this.errorData.availableMemory && this.errorData.requiredMemory) {
      return this.errorData.requiredMemory - this.errorData.availableMemory;
    }
    return undefined;
  }

  async uploadErrorLogs(): Promise<void> {
    this.isUploadingLogs.set(true);
    try {
      const success = await this.loggerService.sendLogsToBackend();
      if (success) {
        this.toast.success(this.translate.translate('browserError.logsUploadedSuccess'));
      } else {
        this.toast.error(this.translate.translate('browserError.logsUploadedError'));
      }
    } catch (error) {
      this.logger.error('Failed to upload error logs:', error);
      this.toast.error(this.translate.translate('browserError.logsUploadedError'));
    } finally {
      this.isUploadingLogs.set(false);
    }
  }
}
